import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject, isStringArray } from "@/shared/utils/typeGuards";

export const kAudioToPresetSearchRoute = "/audio-to-preset-search";

export const kMinSearchResults = 1;
export const kMaxSearchResults = 10;

export type PresetSearchIndex = "all_presets" | "premium_presets" | "instrument_presets";

export const kPresetSearchIndexes: PresetSearchIndex[] = [
    "all_presets",
    "premium_presets",
    "instrument_presets",
];

export const kDefaultPresetSearchIndex: PresetSearchIndex = "all_presets";

export const isPresetSearchIndex = (value: unknown): value is PresetSearchIndex =>
    typeof value === "string" &&
    (value === "all_presets" || value === "premium_presets" || value === "instrument_presets");

export interface AudioToPresetSearchRequestBody {
    fileKey: string;
    k: number;
    index?: PresetSearchIndex;
}

export const isAudioToPresetSearchRequestBody = (
    value: unknown,
): value is AudioToPresetSearchRequestBody =>
    isObject(value) && typeof value.fileKey === "string" && typeof value.k === "number";

/**
 * What the catalog says about a matched preset, when the server could ask:
 * the name and the plugin it is for. Either can be unknown.
 */
export interface PresetSearchItem {
    uuid: string;
    name: string | null;
    device: string | null;
}

export const isPresetSearchItem = (value: unknown): value is PresetSearchItem =>
    isObject(value) &&
    typeof value.uuid === "string" &&
    (value.name === null || typeof value.name === "string") &&
    (value.device === null || typeof value.device === "string");

export interface AudioToPresetSearchSuccessResponse {
    ok: true;
    uuids: string[];
    urls: string[];
    sourceIndexes: string[];
    /**
     * Catalog details for the matches the gateway knew, in no particular
     * order. Absent when the request carried no user token, or when the
     * lookup failed: the match list above stands on its own.
     */
    items?: PresetSearchItem[];
}
export const isAudioToPresetSearchSuccessResponse = (
    response: unknown,
): response is AudioToPresetSearchSuccessResponse =>
    isObject(response) &&
    response.ok === true &&
    isStringArray(response.uuids) &&
    isStringArray(response.urls) &&
    isStringArray(response.sourceIndexes) &&
    (response.items === undefined ||
        (Array.isArray(response.items) && response.items.every(isPresetSearchItem)));

export const kAudioToPresetSearchEndpoint: ApiEndpoint<
    AudioToPresetSearchRequestBody,
    AudioToPresetSearchSuccessResponse
> = {
    route: kAudioToPresetSearchRoute,
    label: "Preset Search",
    source: "Modal",
    category: "Semantic Intent",
    description:
        "Searches the Splice preset library using audio as a semantic query to find sonically similar sounds.",
    isRequestBody: isAudioToPresetSearchRequestBody,
    isSuccessResponse: isAudioToPresetSearchSuccessResponse,
};
